import { flexRender, Table as ReactTable } from "@tanstack/react-table";
import { Table, THead, THeadRow, TBody, TRow, TCell } from "@theprelude/ds";
import React from "react";
import { VirtualSortedHeader } from "./header";
import { getGridTemplateColumns } from "./hooks";
import { LoadingTable } from "./loading-table";
import { TableOptions } from "./table-options";

export function GridTable<TData>({
  className,
  table,
  children,
  loading,
  emptyState,
}: {
  loading?: boolean;
  className?: string;
  table: ReactTable<TData>;
  showFilter?: boolean;
  children?: React.ReactNode;
  emptyState?: React.ReactNode;
}) {
  const gridTemplateColumns = getGridTemplateColumns(table);
  return (
    <Table className={className}>
      <TableOptions table={table} />
      <THead>
        {table.getHeaderGroups().map((headerGroup) => (
          <THeadRow
            key={headerGroup.id}
            style={{
              gridTemplateColumns,
            }}
          >
            {headerGroup.headers.map((header) => (
              <VirtualSortedHeader key={header.id} header={header} />
            ))}
          </THeadRow>
        ))}
      </THead>
      <GridTableBody table={table} loading={loading} emptyState={emptyState} />
      {children}
    </Table>
  );
}

function GridTableBody<TData>({
  table,
  loading,
  emptyState,
}: {
  table: ReactTable<TData>;
  loading?: boolean;
  emptyState?: React.ReactNode;
}) {
  const { rows } = table.getRowModel();
  const gridTemplateColumns = getGridTemplateColumns(table);

  if (loading) {
    return <LoadingTable table={table} rowCount={5} />;
  }

  if (rows.length === 0 && emptyState) {
    return (
      <TBody>
        <TRow>
          <TCell>{emptyState}</TCell>
        </TRow>
      </TBody>
    );
  }

  return (
    <TBody>
      {rows.map((row) => {
        return (
          <TRow
            key={row.id}
            style={{
              gridTemplateColumns,
            }}
          >
            {row.getVisibleCells().map((cell) => {
              return (
                <TCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TCell>
              );
            })}
          </TRow>
        );
      })}
    </TBody>
  );
}
